import {isRunningWorkout, type RunningWorkout} from './runningWorkoutMetrics';

export type ProgressMetric = 'power' | 'wkg' | 'pace' | 'heart_rate';

export function roundedRecordDistance(metres: number | null): number | null {
  return metres != null && Number.isFinite(metres) && metres >= 0
    ? Math.round(metres / 1000)
    : null;
}

/** A small editable UI starting point, not a training prescription. Never auto-save it. */
export function suggestedRecordGoal(
  current: number | null,
  metric: ProgressMetric,
): number | null {
  if (
    current == null ||
    !Number.isFinite(current) ||
    current <= 0 ||
    metric === 'heart_rate'
  )
    return null;
  if (metric === 'pace')
    return current > 1 ? Math.max(1, Math.floor(current * 0.98)) : null;
  const step = metric === 'wkg' ? 0.01 : 5;
  return Number((Math.ceil((current * 1.02) / step - 1e-8) * step).toFixed(2));
}

export function recordsForProgressMetric<
  T extends {
    workout?: (RunningWorkout & {workout_type_id?: number | null}) | null;
  },
>(records: T[], pace: boolean): T[] {
  return pace
    ? records.filter(
        ({workout}) =>
          workout &&
          isRunningWorkout({
            ...workout,
            workout_type: {
              ...workout.workout_type,
              id:
                workout.workout_type?.id ??
                workout.workout_type_id ??
                undefined,
            },
          }),
      )
    : records;
}

export function parseRecordGoal(
  input: string,
  metric: ProgressMetric,
): number | null {
  const raw = input
    .trim()
    .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
    .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
    .replace('٫', '.');
  if (metric === 'heart_rate') return null;
  if (metric === 'pace') {
    const match = /^(\d+):([0-5]\d)$/.exec(raw);
    if (!match) return null;
    const value = Number(match[1]) * 60 + Number(match[2]);
    return value > 0 ? value : null;
  }
  if (!/^\d+(\.\d+)?$/.test(raw)) return null;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
}

export function recordImprovement(
  current: number,
  previous: unknown,
  metric: ProgressMetric,
): number | null {
  // previous_value is an absolute metric from the API, never W/kg.
  if (metric === 'heart_rate' || metric === 'wkg') return null;
  const before = Number(previous);
  if (
    !Number.isFinite(current) ||
    current <= 0 ||
    !Number.isFinite(before) ||
    before <= 0
  )
    return null;
  const improvement =
    ((metric === 'pace' ? before - current : current - before) / before) * 100;
  return improvement > 0 ? improvement : null;
}

export function recordGoalProgress(
  current: number,
  goal: number,
  metric: ProgressMetric,
) {
  if (
    metric === 'heart_rate' ||
    !Number.isFinite(current) ||
    !Number.isFinite(goal) ||
    current <= 0 ||
    goal <= 0
  )
    return null;
  const displayedCurrent = metric === 'pace' ? Math.round(current) : current;
  return {
    achieved: metric === 'pace' ? displayedCurrent <= goal : current >= goal,
    fraction: Math.min(
      1,
      metric === 'pace' ? goal / displayedCurrent : current / goal,
    ),
    remaining: Math.max(
      0,
      metric === 'pace' ? displayedCurrent - goal : goal - current,
    ),
  };
}
